import type { UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  FlattenedItem,
  Projection,
  SortableTreeBaseContainer,
  SortableTreeBaseItem,
  SortableTreeCanDrop,
  SortableTreeContainer,
  SortableTreeNode,
} from './types';

const ROOT_ID = '__sortable_tree_root__';

/** Depth-first traversal that tags each node with its `parentId`/`depth`/`index`. */
export const flatten = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  nodes: SortableTreeNode<TItem, TContainer>[],
  parentId: UniqueIdentifier | null = null,
  depth = 0
): FlattenedItem<TItem, TContainer>[] =>
  nodes.flatMap((node, index) => {
    if (node.type === 'container') {
      const { children, ...containerWithoutChildren } = node;

      return [
        { ...containerWithoutChildren, parentId, depth, index },
        ...flatten(children, node.id, depth + 1),
      ];
    }

    return [{ ...node, parentId, depth, index }];
  });

/** Inverse of {@link flatten}. Nests purely by `parentId` (ignores `depth`). */
export const buildTree = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  flattenedNodes: FlattenedItem<TItem, TContainer>[]
): SortableTreeNode<TItem, TContainer>[] => {
  const root: SortableTreeNode<TItem, TContainer>[] = [];
  // Maps a container id to the `children` array to push its descendants into.
  const childrenById = new Map<
    UniqueIdentifier,
    SortableTreeNode<TItem, TContainer>[]
  >([[ROOT_ID, root]]);

  // Pass 1: materialise each node and register every container's children array,
  // so a parent resolves even when it sits *after* its children in the flat list
  // (e.g. a container dragged below its own descendants). Dropping the flat-list
  // metadata leaves the node's own payload, which we preserve — TS can't prove
  // the remainder is exactly that payload for an arbitrary generic, hence the
  // assertions.
  const placements = flattenedNodes.map((flattenedNode) => {
    if (flattenedNode.type === 'container') {
      const {
        parentId: _parentId,
        depth: _depth,
        index: _index,
        ...containerData
      } = flattenedNode;
      const container = {
        ...containerData,
        children: [],
      } as unknown as SortableTreeContainer<TItem, TContainer>;

      childrenById.set(container.id, container.children);

      return { node: container, parentId: flattenedNode.parentId };
    }

    const {
      parentId: _parentId,
      depth: _depth,
      index: _index,
      ...item
    } = flattenedNode;

    return {
      node: item as unknown as TItem,
      parentId: flattenedNode.parentId,
    };
  });

  // Pass 2: attach each node to its parent, preserving sibling order.
  for (const { node, parentId } of placements) {
    (childrenById.get(parentId ?? ROOT_ID) ?? root).push(node);
  }

  return root;
};

/**
 * Drop the descendants of `ids` from a flattened list, so a dragged container
 * moves as a single block and can't be dropped inside itself.
 */
export const removeChildrenOf = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  nodes: FlattenedItem<TItem, TContainer>[],
  ids: UniqueIdentifier[]
): FlattenedItem<TItem, TContainer>[] => {
  const excludeParentIds = new Set(ids);

  return nodes.filter((node) => {
    if (node.parentId != null && excludeParentIds.has(node.parentId)) {
      if (node.type === 'container') {
        excludeParentIds.add(node.id);
      }

      return false;
    }

    return true;
  });
};

/** Maps each node id to its node, in a single depth-first traversal. */
export const buildNodesById = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  nodes: SortableTreeNode<TItem, TContainer>[]
): Map<UniqueIdentifier, SortableTreeNode<TItem, TContainer>> => {
  const nodesById = new Map<
    UniqueIdentifier,
    SortableTreeNode<TItem, TContainer>
  >();

  const walk = (currentNodes: SortableTreeNode<TItem, TContainer>[]) => {
    for (const node of currentNodes) {
      nodesById.set(node.id, node);

      if (node.type === 'container') {
        walk(node.children);
      }
    }
  };

  walk(nodes);

  return nodesById;
};

/**
 * The 0-based index of `id` among its siblings (nodes sharing `parentId`) in a
 * flattened list. The flat list preserves sibling order, so filtering to the
 * shared parent and locating `id` yields its position within that parent's
 * children. Returns -1 if `id` is not present under `parentId`.
 */
export const siblingIndexOf = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  flattenedNodes: FlattenedItem<TItem, TContainer>[],
  id: UniqueIdentifier,
  parentId: UniqueIdentifier | null
): number =>
  flattenedNodes
    .filter((node) => node.parentId === parentId)
    .findIndex((node) => node.id === id);

const getDragDepth = (offset: number, indentationWidth: number) =>
  Math.round(offset / indentationWidth);

/**
 * Project the active node's drop position from the vertical slot (`overId`) plus
 * the horizontal `offsetLeft`, and decide whether dropping there is legal. Ported
 * from dnd-kit's Tree example, with these Payaca additions:
 *
 * 1. **Type-gated `maxDepth`** — a leaf `previousItem` caps the depth at its own
 *    level (an item is a leaf and can't gain children), so dragging can never
 *    create a new group; a container `previousItem` allows one level deeper.
 * 2. **Validity** — each depth in the slot's range must pass the structural rule
 *    that an item can't live at the top level (`parentId === null`) and the
 *    consumer's optional `canDrop`.
 * 3. **Snapping** — when the offset's depth is rejected, the nearest valid
 *    depth (shallower on a tie) is used instead, so horizontal drift doesn't
 *    reject a slot that has a legal depth. Only when none is valid is the drop
 *    rejected, at the offset's depth, so the marker still renders for feedback.
 *
 * Both ids must resolve in `nodes`; otherwise (the controlled tree changed
 * mid-drag, or `overId` points at a removed droppable) the drop is flagged invalid.
 *
 * `maxDepthLimit` optionally caps nesting depth (the `maxDepth` prop).
 */
export const getProjection = <
  TItem extends SortableTreeBaseItem,
  TContainer extends SortableTreeBaseContainer = SortableTreeBaseContainer,
>(
  nodes: FlattenedItem<TItem, TContainer>[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number,
  maxDepthLimit?: number,
  canDrop?: SortableTreeCanDrop<TItem, TContainer>
): Projection => {
  const overItemIndex = nodes.findIndex((node) => node.id === overId);
  const activeItemIndex = nodes.findIndex((node) => node.id === activeId);
  const activeItem = nodes[activeItemIndex];
  // The controlled tree can change mid-drag (or `overId` can point at a
  // droppable that was removed), leaving an id absent from `nodes`. Reject the
  // drop rather than dereferencing `undefined` / calling `arrayMove` with -1.
  if (!activeItem || overItemIndex === -1) {
    return {
      depth: 0,
      maxDepth: 0,
      minDepth: 0,
      parentId: null,
      validDepths: [],
      isValid: false,
    };
  }
  const newNodes = arrayMove(nodes, activeItemIndex, overItemIndex);
  const previousItem = newNodes[overItemIndex - 1];
  const nextItem = newNodes[overItemIndex + 1];
  const projectedDepth =
    activeItem.depth + getDragDepth(dragOffset, indentationWidth);

  const maxDepth = Math.min(
    getMaxDepth(previousItem),
    maxDepthLimit ?? Number.POSITIVE_INFINITY
  );
  const minDepth = nextItem ? nextItem.depth : 0;
  const offsetDepth = Math.min(Math.max(projectedDepth, minDepth), maxDepth);

  const verdicts = new Map<number, { isValid: boolean; reason?: string }>();
  for (let depth = minDepth; depth <= maxDepth; depth++) {
    verdicts.set(depth, verdictAt(depth));
  }
  const validDepths = [...verdicts]
    .filter(([, verdict]) => verdict.isValid)
    .map(([depth]) => depth);

  if (validDepths.length === 0) {
    return {
      depth: offsetDepth,
      maxDepth,
      minDepth,
      parentId: parentIdAt(offsetDepth),
      validDepths,
      isValid: false,
      reason: verdicts.get(offsetDepth)?.reason,
    };
  }

  // `validDepths` is ascending, so the first of two equally near depths is the
  // shallower one.
  const depth = validDepths.reduce((nearest, candidate) =>
    Math.abs(candidate - offsetDepth) < Math.abs(nearest - offsetDepth)
      ? candidate
      : nearest
  );

  return {
    depth,
    maxDepth,
    minDepth,
    parentId: parentIdAt(depth),
    validDepths,
    isValid: true,
  };

  function verdictAt(depth: number): { isValid: boolean; reason?: string } {
    const parentId = parentIdAt(depth);
    // An item is a leaf: it must live inside a container, never at the top level.
    const isStructurallyValid = activeItem.type !== 'item' || parentId !== null;
    const verdict = canDrop
      ? canDrop({
          active: activeItem,
          parent:
            parentId == null
              ? null
              : (nodes.find((node) => node.id === parentId) ?? null),
          parentId,
          currentParentId: activeItem.parentId,
          depth,
        })
      : true;

    if (typeof verdict === 'object') {
      return { isValid: false, reason: verdict.reason };
    }

    return { isValid: isStructurallyValid && verdict };
  }

  function getMaxDepth(
    prev: FlattenedItem<TItem, TContainer> | undefined
  ): number {
    if (!prev) {
      return 0;
    }

    return prev.type === 'container' ? prev.depth + 1 : prev.depth;
  }

  function parentIdAt(depth: number): UniqueIdentifier | null {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newNodes
      .slice(0, overItemIndex)
      .reverse()
      .find((node) => node.depth === depth)?.parentId;

    return newParent ?? null;
  }
};
